import { useEffect, useRef } from "react";

// Lightweight drifting starfield + glowing particles on a canvas.
export const StarField = ({ color = "#A78BFA" }) => {
  const canvasRef = useRef(null);
  const colorRef = useRef(color);
  colorRef.current = color;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = 0;
    let h = 0;
    let stars = [];
    let orbs = [];

    const seed = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
      const count = Math.min(160, Math.floor((w * h) / 9000));
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 1.4 + 0.3,
        t: Math.random() * Math.PI * 2,
        s: Math.random() * 0.02 + 0.005,
        vy: Math.random() * 0.12 + 0.02,
      }));
      orbs = Array.from({ length: 6 }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * 60 + 40,
        vx: (Math.random() - 0.5) * 0.15,
        vy: (Math.random() - 0.5) * 0.15,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      // soft glowing orbs
      orbs.forEach((o) => {
        o.x += o.vx;
        o.y += o.vy;
        if (o.x < -o.r) o.x = w + o.r;
        if (o.x > w + o.r) o.x = -o.r;
        if (o.y < -o.r) o.y = h + o.r;
        if (o.y > h + o.r) o.y = -o.r;
        const g = ctx.createRadialGradient(o.x, o.y, 0, o.x, o.y, o.r);
        g.addColorStop(0, hexToRgba(colorRef.current, 0.12));
        g.addColorStop(1, hexToRgba(colorRef.current, 0));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(o.x, o.y, o.r, 0, Math.PI * 2);
        ctx.fill();
      });
      // twinkling stars
      stars.forEach((st) => {
        st.t += st.s;
        st.y += st.vy;
        if (st.y > h) st.y = 0;
        const alpha = 0.35 + Math.sin(st.t) * 0.35;
        ctx.fillStyle = hexToRgba(colorRef.current, Math.max(0.08, alpha));
        ctx.beginPath();
        ctx.arc(st.x, st.y, st.r, 0, Math.PI * 2);
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };

    seed();
    draw();
    const onResize = () => seed();
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" data-testid="starfield" />;
};

function hexToRgba(hex, a) {
  const m = hex.replace("#", "");
  const bigint = parseInt(m.length === 3 ? m.split("").map((c) => c + c).join("") : m, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r},${g},${b},${a})`;
}
